import {
    Block,
    type Change,
    FRAGMENT_PARENT_TYPE_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
    type SchemaNormalizeFn,
} from '@gitbook/slate';
import { createOrderIndex } from '@gitbook/util-orderindex';
import { assert } from '@gitbook/util-ts';

import { isHeading } from '../heading';
import {
    BLOCK_COLUMN,
    BLOCK_HINT,
    BLOCK_IF,
    BLOCK_LIST_ITEM,
    BLOCK_PARAGRAPH,
    BLOCK_STEPPER_STEP,
    BLOCK_TABLE,
    BLOCK_TABS_ITEM,
    BLOCK_UPDATE,
    type BlockTableData,
    FRAGMENT_EXPANDABLE_BODY,
    type TableView,
} from '../types';
import { canInsertBlockIn } from '../utils';
import { canUseViewWithCellMerges, setRecordValue } from './changes';
import { columnSpecs } from './columns';
import {
    getCellMergePositions,
    isMergeableColumnDefinition,
    isMergeValueEmpty,
    normalizeCellMerges,
    resolveCellMerges,
} from './merges';
import { defaultRecordSortedKeys } from './utils';
import { viewSpecs } from './views';

/**
 * Table schema.
 */
export const schema = Schema.create({
    validations: [
        (node) => {
            if (node.object !== 'block' || node.type !== BLOCK_TABLE) {
                return;
            }

            return validateTable(node);
        },
    ],
    blocks: {
        [BLOCK_TABLE]: {
            isVoid: true,
            parent: {
                objects: ['document', 'fragment', 'block'],
                types: [
                    undefined,
                    BLOCK_LIST_ITEM,
                    BLOCK_TABS_ITEM,
                    BLOCK_STEPPER_STEP,
                    FRAGMENT_EXPANDABLE_BODY,
                    BLOCK_IF,
                    BLOCK_COLUMN,
                    BLOCK_UPDATE,
                    BLOCK_HINT,
                ],
            },
            fragmentParent: {
                objects: ['document', 'fragment'],
                fragmentTypes: [FRAGMENT_EXPANDABLE_BODY],
            },
            data: {
                records: (records) => {
                    return typeof records === 'object';
                },
                definition: (definition) => {
                    return typeof definition === 'object';
                },
                view: (view) => {
                    return typeof view === 'object';
                },
                // @deprecated Per-block width is no longer consumed by the editor or renderer
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
                // Optional override for the search bar; `undefined` defers to the renderer's default.
                search: (search) => typeof search === 'undefined' || typeof search === 'boolean',
            },
            normalize: (change, violation, context) => {
                switch (violation) {
                    case FRAGMENT_PARENT_TYPE_INVALID:
                    case PARENT_TYPE_INVALID: {
                        const { node } = context;
                        // Hoist invalidly nested tables to the nearest forward position whose parent accepts tables.
                        // This preserves the table without performing backwards writes during normalization.
                        assert(Block.isBlock(node));
                        return hoistInvalidTableForward(change, node);
                    }
                }
            },
        },
    },
});

/**
 * Move an invalidly nested table after the nearest enclosing block whose parent can legally contain tables.
 */
function hoistInvalidTableForward(change: Change, node: Block): Change {
    const ancestors = change.value.document.getAncestors(node.key);

    if (ancestors) {
        const ancestorBlocks = ancestors
            .toArray()
            .reverse()
            .filter((ancestor): ancestor is Block => ancestor.object === 'block');

        for (const ancestor of ancestorBlocks) {
            const parent = change.value.document.getParent(ancestor.key);

            if (parent && canInsertBlockIn(parent, BLOCK_TABLE)) {
                return change.moveAfterNodeByKey(node.key, ancestor.key, { normalize: false });
            }
        }
    }

    return change.removeNodeByKey(node.key);
}

/**
 * Validate and normalize an entire table.
 */
function validateTable(node: Block): undefined | ((change: Change) => void) {
    const data = node.data.toFrozenObject() as BlockTableData;

    // If table has no records or definition, remove the entire table block
    if (!data.records || Object.keys(data.records).length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }
    if (!data.definition || Object.keys(data.definition).length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    const changeDefinition = validateDefinitions(node, data);
    if (changeDefinition) {
        return (change) => {
            change.withoutNormalization(changeDefinition);
        };
    }

    // Only validate records if there are records to validate
    if (Object.keys(data.records).length > 0) {
        const changeRecords = validateRecords(node, data);
        if (changeRecords) {
            return (change) => {
                change.withoutNormalization(changeRecords);
            };
        }
    }

    const changeView = validateView(node, data);
    if (changeView) {
        return (change) => {
            change.withoutNormalization(changeView);
        };
    }

    const changeCellMerges = validateCellMerges(node, data);
    if (changeCellMerges) {
        return (change) => {
            change.withoutNormalization(changeCellMerges);
        };
    }

    const changeBlockAlignment = validateBlockTextAlignment(node);
    if (changeBlockAlignment) {
        return (change) => {
            change.withoutNormalization(changeBlockAlignment);
        };
    }
}

/** Validate horizontal and vertical cell merges against the normalized table shape. */
function validateCellMerges(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    // A table that never had merges has nothing to validate or strip. An empty or malformed
    // `cellMerges` still falls through, so the key below gets removed.
    if (typeof data.cellMerges === 'undefined') return;

    const resolved = resolveCellMerges(data, defaultRecordSortedKeys(data), data.view.columns);
    const normalized = resolved.map((merge) => merge.merge);
    const current = Array.isArray(data.cellMerges) ? data.cellMerges : [];
    const unchanged =
        normalized.length === current.length &&
        normalized.every((merge, index) => merge === current[index]);

    if (unchanged && (typeof data.cellMerges === 'undefined' || current.length > 0)) {
        const populatedCoveredCells = resolved.flatMap((merge) =>
            getCellMergePositions(merge)
                .slice(1)
                .filter((cell) => {
                    const definition = data.definition[cell.definitionId];
                    if (!isMergeableColumnDefinition(definition)) return false;
                    return !isMergeValueEmpty(
                        data.records[cell.recordId]?.values[cell.definitionId],
                        definition,
                        node
                    );
                })
        );

        if (populatedCoveredCells.length === 0) return;

        return (change) => {
            for (const cell of populatedCoveredCells) {
                change.removeDataPathByKey(node.key, [
                    'records',
                    cell.recordId,
                    'values',
                    cell.definitionId,
                ]);
            }
        };
    }

    return (change) => {
        if (normalized.length === 0) {
            change.removeDataByKey(node.key, ['cellMerges']);
        } else {
            change.setDataByKey(node.key, ['cellMerges'], normalized);
        }
    };
}

/**
 * Validates the definition of the table.
 */
function validateDefinitions(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    const changes: SchemaNormalizeFn[] = [];

    const definitionEntries = Object.entries(data.definition);

    // Table should have at lease one definition
    if (definitionEntries.length === 0) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    definitionEntries.forEach(([colId, colDef]) => {
        const colValidation = columnSpecs[colDef.type];

        if (!colValidation) {
            changes.push((change) => {
                change.removeDataPathByKey(node.key, ['definition', colId]);
            });
            return;
        }

        if (typeof colDef.title !== 'string') {
            changes.push((change) => {
                change.setDataByKey(node.key, ['definition', colId, 'title'], '');
            });
        }

        if (!colValidation.normalizeDefinition) {
            return;
        }

        const result = colValidation.normalizeDefinition(colDef);
        if (typeof result !== 'undefined' && result !== colDef) {
            changes.push((change) => {
                if (result) {
                    change.setDataByKey(
                        node.key,
                        ['definition', colId],
                        typeof colDef.title !== 'string' ? { ...result, title: '' } : result
                    );
                } else {
                    change.removeDataPathByKey(node.key, ['definition', colId]);
                }
            });
        }
    });

    return changes.length === 0
        ? undefined
        : (change) => {
              changes.forEach((callback) => {
                  callback(change);
              });
          };
}

/**
 * Validates the records of a table.
 */
function validateRecords(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    const recordEntries = Object.entries(data.records);

    const changes: SchemaNormalizeFn[] = [];

    /** Order index already allocated */
    const allocatedOrderIndexes: string[] = [];

    // Verify that all Records contain a valid column entry.
    const colIds = Object.keys(data.definition);

    /** Fragments that are currently in the table */
    const existingFragmentKeys = new Set(node.fragments.toKeysArray());
    const validatedFragmentKeys = new Set<string>();
    const recordToRecheckForFragments = new Set<string>();

    recordEntries.forEach(([key, record]) => {
        // Records should have an orderable index
        if (typeof record.orderIndex !== 'string') {
            changes.push((change) => {
                change.insertDataByKey(
                    node.key,
                    ['records', key, 'orderIndex'],
                    createOrderIndex(
                        allocatedOrderIndexes.length > 0
                            ? { before: allocatedOrderIndexes[allocatedOrderIndexes.length - 1] }
                            : {}
                    ),
                    { normalize: false }
                );
            });
        } else {
            let indexToAllocate = record.orderIndex;

            // And it should be unique
            if (allocatedOrderIndexes.includes(record.orderIndex)) {
                indexToAllocate = createOrderIndex({
                    before: allocatedOrderIndexes[allocatedOrderIndexes.length - 1],
                });

                changes.push((change) => {
                    change.insertDataByKey(
                        node.key,
                        ['records', key, 'orderIndex'],
                        indexToAllocate,
                        { normalize: false }
                    );
                });
            }

            allocatedOrderIndexes.push(indexToAllocate);
        }

        if (record.values) {
            // Remove any record values that do not have a valid column definition.
            const unknownRecordValues = Object.keys(record.values).filter(
                (key) => !(key in data.definition)
            );

            if (unknownRecordValues.length > 0) {
                changes.push((change) => {
                    unknownRecordValues.forEach((recordValue) => {
                        change.removeDataPathByKey(node.key, [
                            'records',
                            key,
                            'values',
                            recordValue,
                        ]);
                    });
                });
            }
        }

        // Validate the column
        colIds.forEach((colId) => {
            const colDef = data.definition[colId];
            const value = record.values?.[colDef.id];
            const colValidation = columnSpecs[colDef.type];

            // Delegate to the column validation for validating/normalizing the value itself.
            const validatedValue = colValidation.normalize(key, value, colDef, node);

            if (validatedValue !== undefined && validatedValue !== value) {
                recordToRecheckForFragments.add(key);
                changes.push((change) => {
                    const result =
                        typeof validatedValue === 'function'
                            ? validatedValue(change)
                            : validatedValue;
                    if (typeof result !== 'undefined') {
                        setRecordValue(change, node.key, key, colDef.id, result);
                    }
                });
            } else {
                // Keep track of the valid fragment keys.
                if (colValidation.fragmentKeys) {
                    const validFragmentKeys = colValidation.fragmentKeys(key, value, colDef, node);
                    validFragmentKeys.forEach((fragmentKey) => {
                        if (
                            !existingFragmentKeys.has(fragmentKey) &&
                            !validatedFragmentKeys.has(fragmentKey)
                        ) {
                            throw new Error(
                                `Normalization should have created the fragment ${fragmentKey} for record ${key} and column ${colDef.id}`
                            );
                        }
                        existingFragmentKeys.delete(fragmentKey);
                        validatedFragmentKeys.add(fragmentKey);
                    });
                }
            }
        });
    });

    // Remove any fragments not actively referenced by a record.
    if (existingFragmentKeys.size > 0) {
        changes.push((change) => {
            // Recheck the fragments for the records that have been modified.
            recordToRecheckForFragments.forEach((recordKey) => {
                const record = data.records[recordKey];
                const recordValues = record.values;
                const recordValuesKeys = Object.keys(recordValues);
                recordValuesKeys.forEach((recordValueKey) => {
                    const recordValue = recordValues[recordValueKey];
                    const colDef = data.definition[recordValueKey];
                    const colValidation = columnSpecs[colDef.type];
                    if (colValidation.fragmentKeys) {
                        const validFragmentKeys = colValidation.fragmentKeys(
                            recordKey,
                            recordValue,
                            colDef,
                            node
                        );
                        validFragmentKeys.forEach((fragmentKey) => {
                            if (
                                !existingFragmentKeys.has(fragmentKey) &&
                                !validatedFragmentKeys.has(fragmentKey)
                            ) {
                                throw new Error(
                                    `Normalization should have created the fragment ${fragmentKey} for record ${recordKey} and column ${recordValueKey}`
                                );
                            }
                            existingFragmentKeys.delete(fragmentKey);
                            validatedFragmentKeys.add(fragmentKey);
                        });
                    }
                });
            });

            existingFragmentKeys.forEach((fragmentKey) => {
                change.removeFragmentInParentByKey(node.key, fragmentKey);
            });
        });
    }

    return changes.length === 0
        ? undefined
        : (change) => {
              changes.forEach((callback) => {
                  callback(change);
              });
          };
}

/**
 * Validates the view of a Table.
 */
function validateView(node: Block, data: BlockTableData): undefined | SchemaNormalizeFn {
    if (!data.view) {
        return (change) => {
            const defaultView: TableView = {
                type: 'grid',
                columns: [],
                hideHeader: true,
                stickyHeader: false,
                stickyFirstColumn: false,
                useNewSizing: true,
            };
            change.setDataByKey(node.key, ['view'], defaultView);
        };
    }

    // Only a non-grid view can be invalidated by merges, so the grid view - what every table
    // starts on - never needs them resolved.
    const view =
        data.view.type === 'grid' ||
        canUseViewWithCellMerges({ cellMerges: normalizeCellMerges(data) }, data.view.type)
            ? data.view
            : { ...data.view, type: 'grid' as const };
    const viewValidation = viewSpecs[view.type];

    const normalized = viewValidation ? viewValidation.normalize(view, data) : null;

    if (normalized === null) {
        return (change) => {
            change.removeNodeByKey(node.key);
        };
    }

    if (typeof normalized !== 'undefined' && normalized !== data.view) {
        return (change) => {
            change.setDataByKey(node.key, ['view'], normalized);
        };
    }

    return;
}

/**
 * Ensure paragraphs/headings within table fragments do not have alignment defined.
 */
function validateBlockTextAlignment(node: Block): undefined | SchemaNormalizeFn {
    const paragraphs = new Set<string>();

    node.fragments.forEach((fragment) => {
        fragment.nodes.forEach((block) => {
            if ((block.type === BLOCK_PARAGRAPH || isHeading(block)) && block.data.has('align')) {
                paragraphs.add(block.key);
            }
        });
    });

    return paragraphs.size === 0
        ? undefined
        : (change) => {
              paragraphs.forEach((key) => {
                  change.removeDataByKey(key, ['align']);
              });
          };
}
